import axiosInstance from "@/lib/axiosInstance";
import { entitiesEndpoints } from "./entities.endpoints";
import { sampleEntities, type Entity } from "@/components/data/entities";
import { buildFilterByParam } from "@/utils/buildFilterByParam";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import type {
  EntityDropdownOption,
  EntityDropdownResponse,
  EntityLabelDropdownOptions,
  EntityListQueryOptions,
  EntityListResponse,
  FrequentEntitiesResponse,
  MyEntitiesResponse,
  MyEntity,
} from "./entities.queries.types";
import type { IEntityDetailsResponse, TEntityDetails } from "./entities.mutations.types";

export const EntitiesQueryKeys = {
  list: "entitiesList",
  dropdown: "entitiesDropdown",
  entityLabelDropdown: "entityLabelDropdown",
  frequentEntities: "entityFrequentEntities",
  myEntities: "myEntities",
  details: "details",
} as const;

// Shared static options for entity filters
export const ENTITY_TYPE_OPTIONS = [
  { value: "internal", label: "Internal" },
  { value: "external", label: "External" },
] as const;

// SERV-3180: each value is stored verbatim and matched verbatim, so neither the create form
// nor the list filter may reshape it.
export const INTERNAL_CATEGORY_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
] as const;

export const EXTERNAL_CATEGORY_OPTIONS = [
  { value: "Direct Client", label: "Direct Client" },
  { value: "Facility Management Company (FMC)", label: "Facility Management Company (FMC)" },
  { value: "Property Management Company (PMC)", label: "Property Management Company (PMC)" },
] as const;

export const ENTITY_CATEGORY_OPTIONS = [
  ...INTERNAL_CATEGORY_OPTIONS,
  ...EXTERNAL_CATEGORY_OPTIONS,
] as const;

// TODO: Replace this with a real API call using axiosInstance when the
// backend endpoint for entities is available.
export const getEntities = async (): Promise<Entity[]> => {
  return sampleEntities;
};

export const getEntitiesList = async (
  options: EntityListQueryOptions,
  signal: AbortSignal,
): Promise<EntityListResponse> => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.pageSize.toString());

  if (options.search) {
    params.append("search", options.search);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  // const cleanFilter = Object.fromEntries(
  //   Object.entries(options.filters ?? {}).filter(
  //     ([_, value]) => value !== undefined && value !== "",
  //   ),
  // );

  // if (Object.keys(cleanFilter).length > 0) {
  //   params.append("filter", JSON.stringify(cleanFilter));
  // }

  // if (options.filterBy && options.filterValue) {
  //   params.append("filterBy", options.filterBy);
  //   params.append("filterValue", options.filterValue.id);
  // }

  // SERV-3180: the match is case sensitive and the external categories are stored capitalised,
  // so these are sent as the option holds them rather than lowercased.
  const filterBy = buildFilterByParam(options.filters, {
    getValue: (f) =>
      f.filterBy === "entityType" || f.filterBy === "category"
        ? f.filterValue.value || f.filterValue.id
        : f.filterValue.id,
  });
  if (filterBy) {
    params.append("filterBy", filterBy);
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);
  const fullUrl = `${entitiesEndpoints.entityList}?${queryString}`;

  const response = await axiosInstance.get<EntityListResponse>(fullUrl, {
    signal,
  });

  return response.data;
};

export const getEntityDropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
): Promise<any> => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  // For entityName: /entity/dropdown?type=entity&search=fe
  // Backend response: {success: true, data: {data: [...], total, page, pageSize, totalPages}}
  // We'll normalize this below to match the standard dropdown shape.
  if (key == "entityName") {
    params.append("type", "entity");
  }

  // For entityType: /dropdown?type=LookupTypes&limit=10&field=label&field=value&filterBy=type&filterValue=user_entity_type
  // Response: {success: true, data: [...], pagination: {...}}
  if (key == "entityType") {
    params.append("type", "LookupTypes");
    params.append("limit", "10");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "user_entity_type");
  }

  // For category: /dropdown?type=LookupTypes&limit=10&field=label&field=value&filterBy=type&filterValue=user_category
  // Response: {success: true, data: [...], pagination: {...}}
  if (key == "category") {
    params.append("type", "LookupTypes");
    params.append("limit", "10");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "user_category");
  }

  // For hqAddress: /dropdown?type=GeographyCities&limit=10&search=m&field=city&field=stateCode&modify={"source":["city", "stateCode"], "target": "value", "delimiter":", "}
  // Response: {success: true, data: [...], pagination: {...}}
  if (key == "hqAddress") {
    params.append("type", "GeographyCities");
    params.append("limit", "10");
    params.append("field", "city");
    params.append("field", "stateCode");
    params.append(
      "modify",
      encodeURI(`{"source":["city", "stateCode"], "target": "value", "delimiter":", "}`),
    );
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  // Decide which endpoint to hit based on key, but keep a single HTTP call
  const baseUrl =
    key === "entityName" ? entitiesEndpoints.entityNameDropdown : entitiesEndpoints.entityDropdown;

  const { data } = await axiosInstance.get<any>(`${baseUrl}?${queryString}`, {
    signal,
  });

  // Normalize /entity/dropdown response to the standard dropdown shape
  if (key === "entityName") {
    const typed = data as EntityDropdownResponse;
    const inner = typed.data;

    return {
      success: typed.success,
      data: inner.data,
      pagination: {
        limit: inner.pageSize,
        nextCursor: null,
        hasMore: inner.page < inner.totalPages,
      },
    };
  }

  // For LookupTypes and GeographyCities, backend already matches the standard shape
  return data;
};

export const validateEntityName = async (entityName: string): Promise<boolean> => {
  const trimmed = entityName.trim();
  if (!trimmed) return false;

  const { data } = await axiosInstance.post(entitiesEndpoints.entityNameValidation, {
    entityName: trimmed,
  });

  // Expected shape: { success: boolean, data: { available: boolean, message: string } }
  if (typeof data?.data?.available === "boolean") {
    return data.data.available;
  }

  // Be tolerant of alternative shapes just in case
  if (typeof data === "boolean") return data;
  if (typeof data?.isAvailable === "boolean") return data.isAvailable;
  if (typeof data?.available === "boolean") return data.available;

  return false;
};

export const validateSubDomain = async (subDomain: string): Promise<boolean> => {
  const trimmed = subDomain.trim();
  if (!trimmed) return false;

  const { data } = await axiosInstance.post(entitiesEndpoints.subDomainValidation, {
    subDomain: trimmed,
  });

  // Expected shape: { success: boolean, data: { available: boolean, message: string } }
  if (typeof data?.data?.available === "boolean") {
    return data.data.available;
  }

  // Be tolerant of alternative shapes just in case
  if (typeof data === "boolean") return data;
  if (typeof data?.isAvailable === "boolean") return data.isAvailable;
  if (typeof data?.available === "boolean") return data.available;

  return false;
};

// Without an explicit pageSize the backend returns 10 rows, which silently hid
// every entity past the tenth in the picker (SERV-2729). 50 is enough that a
// two or three letter search lands on the entity at 2,000 entities.
export const ENTITY_LABEL_PAGE_SIZE = 50;

export const getEntityLabelDropdown = async (
  { search = "", pageSize = ENTITY_LABEL_PAGE_SIZE }: EntityLabelDropdownOptions = {},
  signal?: AbortSignal,
): Promise<EntityDropdownResponse["data"]> => {
  const params = new URLSearchParams();
  params.append("type", "entity");
  params.append("pageSize", String(pageSize));
  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    params.append("search", trimmedSearch);
  }
  const queryString = getQueryParamsWithCustomQueryObj(params);
  const response = await axiosInstance.get<EntityDropdownResponse>(
    `${entitiesEndpoints.entityNameDropdown}?${queryString}`,
    { signal },
  );
  return response.data.data;
};

export const getFrequentEntities = async (
  signal?: AbortSignal,
): Promise<EntityDropdownOption[]> => {
  const response = await axiosInstance.get<FrequentEntitiesResponse>(
    entitiesEndpoints.frequentEntities,
    { signal },
  );
  return response.data.data.data;
};

export const getMyEntities = async (signal?: AbortSignal): Promise<MyEntity[]> => {
  const response = await axiosInstance.get<MyEntitiesResponse>(entitiesEndpoints.myEntities, {
    signal,
  });
  return response.data.data ?? [];
};

// A super admin can add a lead to a new entity while she is signed in; five
// minutes bounds how long the switcher can miss it without a reload.
const MY_ENTITIES_STALE_TIME_MS = 5 * 60 * 1000;

// Shared by the hook and the route guard, which reads the same entry outside React.
export const myEntitiesQueryOptions = (userId: string | null) => ({
  queryKey: [EntitiesQueryKeys.myEntities, userId] as const,
  queryFn: ({ signal }: { signal?: AbortSignal }) => getMyEntities(signal),
  staleTime: MY_ENTITIES_STALE_TIME_MS,
});

export const getEntityDetails = async (entityId: string): Promise<TEntityDetails> => {
  const url = `${entitiesEndpoints.details}/${entityId}`;

  const response = await axiosInstance.get<IEntityDetailsResponse>(url);
  return response.data.data;
};
